import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FieldArray, ErrorMessage } from 'formik'
import { sortBy } from 'lodash'

import { getWagePremiumName, hasAccessToThis } from 'services/Utilities'
import { closeModal } from 'store/actions/modals'
import { Input, Checkbox } from 'components/Forms'
import { Button as MerinioButton, ButtonGroup, Card, RouterLink, Alert, Dropdown } from 'ui'

import { t } from 'translations'

const WagePremiums = ({ wagePremiums = [], setFieldValue, punchRuleId }) => {
  const { wagePremiums: wagePremiumTypes, wagePremiumsById: wagePremiumTypesById, punchRulesById } = useSelector(state => state.resources)
  const { enableWagePremiums } = useSelector(state => state.settings)
  const dispatch = useDispatch()

  if (!enableWagePremiums) return null

  return (
    <>
      <h5>{t('manager.schedule.eventForm.WAGEPREMIUMS.TITLE')}</h5>
      <small className="form-text text-muted">{t('manager.schedule.eventForm.WAGEPREMIUMS.SUMMARY')}</small>

      <FieldArray
        name="wagePremiums"
        render={arrayHelpers => (
          <div className="mb-3">
            <Card className="bg-light">
              <div className="p-3">
                {wagePremiums.map((wagePremium, index) => {
                  const { _id, fullShift, earningCode, unitBased } = wagePremium
                  const isOvertimeWagePremium = punchRuleId && punchRulesById[punchRuleId].overtimeWagePremiumId === _id

                  const preventOverridingAutoOvertime = isOvertimeWagePremium && !hasAccessToThis('wagePremiumOverride')

                  return (
                    <React.Fragment key={index}>
                      <div className="d-flex align-items-center">
                        <div className="flex-even">
                          <div>
                            {getWagePremiumName(_id)} <b>{earningCode}</b>
                          </div>
                          {!unitBased ? (
                            <Checkbox
                              disabled={preventOverridingAutoOvertime}
                              module="manager.schedule.eventForm"
                              name={`wagePremiums[${index}].fullShift`}
                              translation="wagePremiumFullShift"
                            />
                          ) : null}
                        </div>
                        <div className="flex-even ml-3 mr-2">
                          {!unitBased && !fullShift && (
                            <Input
                              module="manager.schedule.eventForm"
                              name={`wagePremiums[${index}].hours`}
                              translation="wagePremiumHours"
                              required
                              type="number"
                              min="0"
                              max="24"
                              step="any"
                              inputGroup={
                                <div className="input-group-append">
                                  <span className="input-group-text">h</span>
                                </div>
                              }
                              disabled={fullShift || preventOverridingAutoOvertime}
                            />
                          )}

                          <Alert variant="info" show={!!fullShift}>
                            {t('manager.schedule.eventForm.WAGE_PREMIUM_FULL_SHIFT_CALCULATED_ON_SUBMIT')}
                          </Alert>

                          {!!unitBased && (
                            <Input
                              module="manager.schedule.eventForm"
                              name={`wagePremiums[${index}].units`}
                              translation="wagePremiumUnits"
                              required
                              disabled={preventOverridingAutoOvertime}
                              type="number"
                              min="0"
                              step="any"
                              inputGroup={
                                <div className="input-group-append">
                                  <span className="input-group-text">
                                    {wagePremiumTypesById[_id]?.unitaryAmountType || t('manager.schedule.eventForm.WAGEPREMIUMUNITS.UNITS')}
                                  </span>
                                </div>
                              }
                            />
                          )}
                        </div>
                        {preventOverridingAutoOvertime ? null : (
                          <div>
                            <MerinioButton
                              type="button"
                              color="secondary"
                              size="small"
                              className="array-input-button"
                              onClick={() => {
                                if (isOvertimeWagePremium) setFieldValue('wagePremiumOverrideOvertime', true)
                                arrayHelpers.remove(index)
                              }}
                              outlined
                            >
                              <i className="fas fa-trash" />
                            </MerinioButton>
                          </div>
                        )}
                      </div>
                      {index !== wagePremiums.length - 1 ? <hr /> : null}
                    </React.Fragment>
                  )
                })}
                <ButtonGroup>
                  <Dropdown.Toggle color="secondary" outlined>
                    <i className="fas fa-plus mr-1" />
                    {t('manager.schedule.eventForm.WAGEPREMIUMS.ADD_WAGE_PREMIUM')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu direction="left">
                    {sortBy(wagePremiumTypes, 'name')
                      .filter(({ _id }) => {
                        const isOvertimeWagePremium = punchRuleId && punchRulesById[punchRuleId].overtimeWagePremiumId === _id
                        if (!isOvertimeWagePremium) return true
                        // Only show the overtime wage premium if the user has the permission to override them.
                        return hasAccessToThis('wagePremiumOverride')
                      })
                      .map(
                        ({
                          _id,
                          name,
                          earningCode,
                          hoursAppliedTo,
                          defaultPredeterminedHours,
                          defaultUnitaryAmount,
                          deductFromRegularHours,
                          countAsOvertime,
                          hasHourMultiplier,
                          hourMultiplier
                        }) => (
                          <Dropdown.Item
                            key={_id}
                            onClick={() => {
                              let newPremium = { _id, earningCode, hours: '', deductFromRegularHours, countAsOvertime, fullShift: false }
                              if (hoursAppliedTo === 'PRE_DETERMINED') newPremium.hours = defaultPredeterminedHours
                              else if (hoursAppliedTo === 'FULL_SHIFT') newPremium.fullShift = true
                              else if (hoursAppliedTo === 'UNIT_BASED') {
                                newPremium.unitBased = true
                                newPremium.units = defaultUnitaryAmount
                              }
                              if (hasHourMultiplier) newPremium.hourMultiplier = hourMultiplier
                              arrayHelpers.push(newPremium)
                            }}
                            disabled={wagePremiums.map(({ _id }) => _id).includes(_id)}
                          >
                            {name} <b>{earningCode}</b>
                          </Dropdown.Item>
                        )
                      )}
                    {!wagePremiumTypes.length && (
                      <div className="px-3">
                        {t('manager.schedule.eventForm.WAGEPREMIUMS_NOT_CONFIGURED.DESCRIPTION')}
                        <small className="form-text text-muted">
                          {hasAccessToThis('wagePremiumManage') && (
                            <RouterLink to="/admin/wagePremiums" onClick={() => dispatch(closeModal())}>
                              {t('manager.schedule.eventForm.WAGEPREMIUMS_NOT_CONFIGURED.LINK')}
                            </RouterLink>
                          )}
                        </small>
                      </div>
                    )}
                  </Dropdown.Menu>
                </ButtonGroup>
              </div>
            </Card>
            {/* Will work when validation is added to event modal (YUP) */}
            <ErrorMessage name="wagePremiums" render={msg => (Array.isArray(msg) ? null : <span className="text-danger"> {t(msg)}</span>)} />
          </div>
        )}
      />
    </>
  )
}

export default WagePremiums
